import { checkCompatibility } from "../utils/compatibility";
import { isDirectAttachConnection } from "../utils/directAttach";

export function boardStateToSyncPayload(nodes, edges, rigName, rigCables) {
  const deviceNodes = nodes.filter((n) => n.type === "device");
  const boundaryNodes = nodes.filter((n) => n.type === "signalBoundary");

  const deviceIndexMap = new Map();
  deviceNodes.forEach((node, index) => deviceIndexMap.set(node.id, index));

  const boundaryIndexMap = new Map();
  boundaryNodes.forEach((node, index) => boundaryIndexMap.set(node.id, index));

  const rig_devices = deviceNodes.map((node) => ({
    device_id: node.data.device.id,
    position_x: node.position.x,
    position_y: node.position.y,
    label: node.data.label || null,
    excluded_ports: node.data.excludedPorts || {},
  }));

  const signal_boundaries = boundaryNodes.map((node) => {
    const b = node.data.boundary;
    return {
      label: b.label,
      direction: b.direction,
      position_x: node.position.x,
      position_y: node.position.y,
      connector: b.connector || null,
      signal_type: b.signal_type || null,
      signal_level: b.signal_level || null,
      phantom_power: b.phantom_power || null,
    };
  });

  // Collect rig_cable refs actually used by edges
  const usedRefs = new Set();
  for (const edge of edges) {
    const chain = edge.data?.cableChain || [];
    for (const entry of chain) {
      const ref = entry.rigCableId || entry.rigCableTempId;
      if (ref) usedRefs.add(ref);
    }
  }

  // Build rig_cables array, excluding orphans
  const rigCableIndexMap = new Map();
  const rig_cables = [];
  for (const rc of (rigCables || [])) {
    const ref = rc.id || rc.tempId;
    if (!usedRefs.has(ref)) continue;
    rigCableIndexMap.set(ref, rig_cables.length);
    rig_cables.push({
      cable_id: rc.cable?.id || rc.cableId,
      label: rc.label || null,
    });
  }

  const connections = edges.map((edge) => {
    const sourceIsBoundary = boundaryIndexMap.has(edge.source);
    const targetIsBoundary = boundaryIndexMap.has(edge.target);
    const isPower = edge.data?.connectionType === "power";

    const plugs = [];

    // Source plug (input direction)
    if (sourceIsBoundary) {
      plugs.push({ boundary_index: boundaryIndexMap.get(edge.source), direction: "input" });
    } else if (isPower) {
      const outputId = parseInt(edge.sourceHandle.replace("power-out-", ""), 10);
      plugs.push({
        device_index: deviceIndexMap.get(edge.source),
        power_output_id: outputId,
        direction: "input",
      });
    } else {
      plugs.push({
        device_index: deviceIndexMap.get(edge.source),
        device_port_id: parseInt(edge.sourceHandle, 10),
        direction: "input",
      });
    }

    // Target plug (output direction)
    if (targetIsBoundary) {
      plugs.push({ boundary_index: boundaryIndexMap.get(edge.target), direction: "output" });
    } else if (isPower) {
      plugs.push({
        device_index: deviceIndexMap.get(edge.target),
        direction: "output",
        selected_voltage: edge.data?.selectedVoltage || null,
      });
    } else {
      plugs.push({
        device_index: deviceIndexMap.get(edge.target),
        device_port_id: parseInt(edge.targetHandle, 10),
        direction: "output",
      });
    }

    const conn = { plugs };

    // Link to rig_cable chain
    const cableChain = edge.data?.cableChain || [];
    if (cableChain.length > 0) {
      conn.rig_cable_indices = cableChain
        .map((entry) => rigCableIndexMap.get(entry.rigCableId || entry.rigCableTempId))
        .filter((idx) => idx !== undefined);
    }

    conn.supplied_cable = edge.data?.suppliedCable || false;

    return conn;
  });

  const labelNodes = nodes.filter((n) => n.type === "canvasLabel");
  const canvas_labels = labelNodes.map((node) => {
    const cl = node.data.canvasLabel;
    return {
      label: cl.label,
      description: cl.description || null,
      position_x: node.position.x,
      position_y: node.position.y,
      width: node.measured?.width ?? node.width ?? node.style?.width ?? cl.width ?? 300,
      height: node.measured?.height ?? node.height ?? node.style?.height ?? cl.height ?? 200,
      color: cl.color || "slate",
      sort_order: cl.sort_order || 0,
    };
  });

  return { name: rigName, rig_devices, rig_cables, connections, signal_boundaries, canvas_labels };
}

export function syncResponseToBoardState(response) {
  const rigDeviceIdMap = new Map();
  const nodeMap = new Map();

  const deviceNodes = (response.rig_devices || []).map((rd) => {
    const nodeId = `rd-${rd.id}`;
    rigDeviceIdMap.set(rd.id, nodeId);

    const node = {
      id: nodeId,
      type: "device",
      position: { x: Number(rd.position_x), y: Number(rd.position_y) },
      data: {
        device: rd.device,
        label: rd.label || null,
        excludedPorts: rd.excluded_ports || {},
      },
    };
    nodeMap.set(nodeId, node);
    return node;
  });

  const boundaryIdMap = new Map();
  const boundaryNodes = (response.signal_boundaries || []).map((sb) => {
    const nodeId = `sb-${sb.id}`;
    boundaryIdMap.set(sb.id, nodeId);

    return {
      id: nodeId,
      type: "signalBoundary",
      position: { x: Number(sb.position_x), y: Number(sb.position_y) },
      data: { boundary: sb },
    };
  });

  // Build rig_cable lookup
  const rigCableMap = new Map();
  const rigCables = (response.rig_cables || []).map((rc) => {
    rigCableMap.set(rc.id, rc);
    return {
      id: rc.id,
      cableId: rc.cable?.id,
      cable: rc.cable,
      label: rc.label,
    };
  });

  const edges = [];

  for (const conn of (response.connections || [])) {
    const plugs = conn.plugs || [];
    const inputPlug = plugs.find((p) => p.direction === "input");
    const outputPlug = plugs.find((p) => p.direction === "output");
    if (!inputPlug || !outputPlug) continue;

    const hasPowerOutput = inputPlug.power_output_id != null;

    const rigCableIds = conn.rig_cable_ids || [];
    const cableChain = rigCableIds
      .map((id) => rigCableMap.get(id))
      .filter(Boolean)
      .map((rc) => ({ rigCableId: rc.id, cableId: rc.cable?.id, cable: rc.cable }));

    // Resolve source
    let sourceId, sourceHandle;
    if (inputPlug.signal_boundary_id) {
      sourceId = boundaryIdMap.get(inputPlug.signal_boundary_id);
      sourceHandle = `boundary-${inputPlug.signal_boundary_id}`;
    } else if (hasPowerOutput) {
      sourceId = rigDeviceIdMap.get(inputPlug.rig_device_id);
      sourceHandle = `power-out-${inputPlug.power_output_id}`;
    } else {
      sourceId = rigDeviceIdMap.get(inputPlug.rig_device_id);
      sourceHandle = String(inputPlug.device_port_id);
    }

    // Resolve target
    let targetId, targetHandle;
    if (outputPlug.signal_boundary_id) {
      targetId = boundaryIdMap.get(outputPlug.signal_boundary_id);
      targetHandle = `boundary-${outputPlug.signal_boundary_id}`;
    } else if (hasPowerOutput) {
      targetId = rigDeviceIdMap.get(outputPlug.rig_device_id);
      const targetNode = nodeMap.get(targetId);
      const targetDeviceId = targetNode?.data?.device?.id;
      targetHandle = `power-in-${targetDeviceId}`;
    } else {
      targetId = rigDeviceIdMap.get(outputPlug.rig_device_id);
      targetHandle = String(outputPlug.device_port_id);
    }

    if (!sourceId || !targetId) continue;

    // Build edge data
    const connectionType = hasPowerOutput ? "power" : "signal";
    const edgeData = { connectionType };

    if (cableChain.length > 0) {
      edgeData.cableChain = cableChain;
    }

    if (conn.supplied_cable) edgeData.suppliedCable = true;
    if (outputPlug.selected_voltage) edgeData.selectedVoltage = outputPlug.selected_voltage;

    // Detect attached power cable on the receiving device
    if (hasPowerOutput && outputPlug.rig_device_id) {
      const targetNodeId = rigDeviceIdMap.get(outputPlug.rig_device_id);
      const targetNode = nodeMap.get(targetNodeId);
      if (targetNode?.data?.device?.attached_power_cable) {
        edgeData.attachedCable = true;
      }
    }

    // Detect attached signal cable on the source port
    if (!hasPowerOutput && inputPlug.rig_device_id && inputPlug.device_port_id) {
      const sourceNodeId = rigDeviceIdMap.get(inputPlug.rig_device_id);
      const sourceNode = nodeMap.get(sourceNodeId);
      const sourcePorts = sourceNode?.data?.device?.device_ports || [];
      const port = sourcePorts.find((p) => p.id === inputPlug.device_port_id);
      if (port?.attached_cable) {
        edgeData.attachedCable = true;
      }
    }

    // Detect direct-attach signal connections
    if (
      !hasPowerOutput &&
      !edgeData.attachedCable &&
      inputPlug.rig_device_id && inputPlug.device_port_id &&
      outputPlug.rig_device_id && outputPlug.device_port_id
    ) {
      const fromNode = nodeMap.get(rigDeviceIdMap.get(inputPlug.rig_device_id));
      const toNode = nodeMap.get(rigDeviceIdMap.get(outputPlug.rig_device_id));
      const fromPort = fromNode?.data?.device?.device_ports?.find((p) => p.id === inputPlug.device_port_id);
      const toPort = toNode?.data?.device?.device_ports?.find((p) => p.id === outputPlug.device_port_id);
      if (isDirectAttachConnection(fromPort, toPort)) {
        edgeData.directAttach = true;
      }
    }

    const edgeId = `e-${conn.id}`;
    const edge = {
      id: edgeId,
      source: sourceId,
      target: targetId,
      sourceHandle,
      targetHandle,
      data: edgeData,
    };

    // Apply compatibility checks for signal connections
    if (!hasPowerOutput) {
      const sourceNode = nodeMap.get(sourceId);
      const targetNode = nodeMap.get(targetId);
      if (sourceNode?.type === "device" && targetNode?.type === "device") {
        const sourcePorts = sourceNode.data.device.device_ports || [];
        const targetPorts = targetNode.data.device.device_ports || [];
        const fromPort = sourcePorts.find((p) => String(p.id) === sourceHandle);
        const toPort = targetPorts.find((p) => String(p.id) === targetHandle);

        if (fromPort && toPort) {
          const result = checkCompatibility(fromPort, toPort, {
            cableChain: edgeData.cableChain,
          });
          if (result.hasErrors) {
            edge.type = "warning";
            edge.data = { ...edgeData, notes: result.notes };
          } else if (result.hasWarns) {
            edge.type = "warn";
            edge.data = { ...edgeData, notes: result.notes };
          } else if (result.hasInfos) {
            edge.type = "info";
            edge.data = { ...edgeData, notes: result.notes };
          } else if (fromPort.connector === "wireless" || toPort.connector === "wireless") {
            edge.type = "wireless";
          }
        }
      }
    }

    edges.push(edge);
  }

  const canvasLabelNodes = (response.canvas_labels || []).map((cl) => {
    const w = Number(cl.width);
    const h = Number(cl.height);
    return {
      id: `cl-${cl.id}`,
      type: "canvasLabel",
      position: { x: Number(cl.position_x), y: Number(cl.position_y) },
      width: w,
      height: h,
      style: { width: w, height: h },
      zIndex: -100 + (cl.sort_order || 0),
      data: { canvasLabel: cl },
    };
  });

  return {
    nodes: [...deviceNodes, ...boundaryNodes, ...canvasLabelNodes],
    edges,
    rigCables,
  };
}
